import { useState } from 'react';
import { apiFetch, useApi } from '../hooks/useApi.js';
import { uiConfirm } from '../utils/dialogs.js';

// ── Shared UI primitives (kept local — same look as StaffTasks.jsx / Clients.jsx) ──
function Btn({ children, variant = 'primary', size = 'md', ...props }) {
  const base = { borderRadius: 'var(--radius)', fontWeight: 600, cursor: 'pointer', border: 'none', fontSize: size === 'sm' ? 12 : 14, padding: size === 'sm' ? '5px 12px' : '9px 18px', opacity: props.disabled ? 0.5 : 1, transition: 'opacity .15s' };
  const v = { primary: { background: 'var(--pld-green)', color: '#fff' }, ghost: { background: 'var(--surface-2)', color: 'var(--text)', border: '1px solid var(--border)' }, danger: { background: '#dc2626', color: '#fff' } };
  return <button {...props} style={{ ...base, ...v[variant], ...props.style }}>{children}</button>;
}

function Modal({ title, onClose, width = 440, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} onClick={onClose}>
      <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', width: '100%', maxWidth: width, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid var(--border)' }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: 'var(--text-muted)', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: 24 }}>{children}</div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{label}</label>
      {children}
      {hint && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{hint}</span>}
    </div>
  );
}

function Input(props) {
  return <input {...props} style={{ padding: '8px 11px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', color: 'var(--text)', ...props.style }} />;
}

function SelectEl({ children, ...props }) {
  return <select {...props} style={{ padding: '8px 11px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', color: 'var(--text)', ...props.style }}>{children}</select>;
}

function Textarea(props) {
  return <textarea {...props} style={{ padding: '8px 11px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', color: 'var(--text)', resize: 'vertical', minHeight: 60, ...props.style }} />;
}

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

function fmtWeekRange(start, end) {
  const s = new Date(start), e = new Date(end);
  const opts = { day: 'numeric', month: 'short' };
  return `${s.toLocaleDateString('en-GB', opts)} – ${e.toLocaleDateString('en-GB', opts)}`;
}

function shiftWeek(dateStr, deltaDays) {
  const d = new Date(dateStr);
  d.setUTCDate(d.getUTCDate() + deltaDays);
  return d.toISOString().slice(0, 10);
}

function dateForDay(weekStart, dayIndex) {
  return shiftWeek(weekStart, dayIndex);
}

const RUN_STATUS_COLOR = {
  planned:     { bg: '#eff6ff', fg: '#1d4ed8' },
  in_progress: { bg: '#fefce8', fg: '#854d0e' },
  paused:      { bg: '#fef2f2', fg: '#991b1b' },
  completed:   { bg: '#f0fdf4', fg: '#166534' },
  signed_off:  { bg: '#f0fdf4', fg: '#166534' },
  cancelled:   { bg: '#f3f4f6', fg: '#6b7280' },
};

// ── Add / Edit lane ──────────────────────────────────────────────────────────
function LaneModal({ lane, onClose, onSaved }) {
  const [name, setName] = useState(lane?.name ?? '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  async function save() {
    if (!name.trim()) { setErr('Name is required'); return; }
    setSaving(true);
    setErr(null);
    try {
      if (lane) {
        await apiFetch(`/production-schedule/lanes/${lane.id}`, { method: 'PATCH', body: { name } });
      } else {
        await apiFetch('/production-schedule/lanes', { method: 'POST', body: { name } });
      }
      onSaved();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  }

  async function deactivate() {
    if (!(await uiConfirm(`Remove the "${lane.name}" work area from the board? Existing cards on it are kept, just hidden.`))) return;
    setSaving(true);
    try {
      await apiFetch(`/production-schedule/lanes/${lane.id}`, { method: 'PATCH', body: { isActive: false } });
      onSaved();
    } catch (e) { setErr(e.message); setSaving(false); }
  }

  return (
    <Modal title={lane ? 'Rename Work Area' : 'New Work Area'} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {err && <div style={{ color: '#dc2626', fontSize: 13 }}>{err}</div>}
        <Field label="Work Area Name" hint="e.g. 1200L Still, 200L Still, Mash Vessel, SSFV1">
          <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Bottling" autoFocus />
        </Field>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          {lane ? <Btn variant="danger" disabled={saving} onClick={deactivate}>Remove Work Area</Btn> : <span />}
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
            <Btn disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save'}</Btn>
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ── Add / Edit card ──────────────────────────────────────────────────────────
function EntryModal({ entry, lanes, defaultLaneId, defaultDate, onClose, onSaved }) {
  const [laneId, setLaneId] = useState(entry?.lane_id ?? defaultLaneId ?? (lanes[0]?.id || ''));
  const [entryDate, setEntryDate] = useState(entry?.entry_date?.slice(0, 10) ?? defaultDate);
  const [label, setLabel] = useState(entry?.label ?? '');
  const [productionRunId, setProductionRunId] = useState(entry?.production_run_id ?? '');
  const [notes, setNotes] = useState(entry?.notes ?? '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  // Recent runs to optionally link this card to — best-effort: a user who
  // can see the schedule but not Production simply won't get this list,
  // and can still save a plain free-text card.
  const { data: runsData } = useApi('/production/runs?limit=100');
  const runs = Array.isArray(runsData?.runs) ? runsData.runs : (Array.isArray(runsData) ? runsData : []);

  async function save() {
    if (!label.trim()) { setErr('Label is required'); return; }
    if (!laneId) { setErr('Work area is required'); return; }
    if (!entryDate) { setErr('Date is required'); return; }
    setSaving(true);
    setErr(null);
    try {
      const body = { laneId, entryDate, label, productionRunId: productionRunId || null, notes: notes || null };
      if (entry) {
        await apiFetch(`/production-schedule/entries/${entry.id}`, { method: 'PATCH', body });
      } else {
        await apiFetch('/production-schedule/entries', { method: 'POST', body });
      }
      onSaved();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  }

  async function remove() {
    if (!(await uiConfirm('Remove this card from the board?'))) return;
    setSaving(true);
    try {
      await apiFetch(`/production-schedule/entries/${entry.id}`, { method: 'DELETE' });
      onSaved();
    } catch (e) { setErr(e.message); setSaving(false); }
  }

  return (
    <Modal title={entry ? 'Edit Card' : 'New Card'} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {err && <div style={{ color: '#dc2626', fontSize: 13 }}>{err}</div>}
        <Field label="Label" hint="e.g. Bod Mash (SSFV1), Gin x2, Bottling — Abode">
          <Input value={label} onChange={e => setLabel(e.target.value)} placeholder="What's happening" autoFocus />
        </Field>
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <Field label="Work Area">
              <SelectEl value={laneId} onChange={e => setLaneId(e.target.value)}>
                {lanes.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </SelectEl>
            </Field>
          </div>
          <div style={{ flex: 1 }}>
            <Field label="Date">
              <Input type="date" value={entryDate} onChange={e => setEntryDate(e.target.value)} />
            </Field>
          </div>
        </div>
        <Field label="Linked Production Run" hint="Optional — ties this card to a real run so it isn't just a label">
          <SelectEl value={productionRunId} onChange={e => setProductionRunId(e.target.value)}>
            <option value="">— none —</option>
            {runs.map(r => (
              <option key={r.id} value={r.id}>{r.run_ref} — {r.recipe_name || 'no recipe'} ({r.status})</option>
            ))}
          </SelectEl>
        </Field>
        <Field label="Notes (optional)">
          <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Anything extra worth noting" />
        </Field>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          {entry ? <Btn variant="danger" disabled={saving} onClick={remove}>Delete</Btn> : <span />}
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
            <Btn disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save'}</Btn>
          </div>
        </div>
      </div>
    </Modal>
  );
}

function fmtNum(n) {
  if (n == null || Number.isNaN(Number(n))) return '';
  return Number(n).toLocaleString('en-GB', { maximumFractionDigits: 2 });
}

function shortUnit(u) {
  const m = { litres: 'L', litre: 'L', bottles: 'btl', bottle: 'btl', units: '', unit: '' };
  return m[String(u).toLowerCase()] ?? u;
}

// ── Card ───────────────────────────────────────────────────────────────────
function Card({ entry, onClick }) {
  const runColor = entry.run_status ? (RUN_STATUS_COLOR[entry.run_status] || RUN_STATUS_COLOR.planned) : null;
  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      style={{
        display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px', borderRadius: 8,
        border: '1px solid var(--border)', background: 'var(--surface)', cursor: onClick ? 'pointer' : 'default', marginBottom: 6, font: 'inherit',
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{entry.label}</div>
      {entry.run_ref && (
        <div style={{ marginTop: 4, display: 'inline-block', fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 5, background: runColor.bg, color: runColor.fg }}>
          {entry.run_ref}{entry.run_recipe_name ? ` · ${entry.run_recipe_name}` : ''}
        </div>
      )}
      {entry.notes && <div style={{ marginTop: 4, fontSize: 11, color: 'var(--text-muted)' }}>{entry.notes}</div>}
      {entry.outputs?.length > 0 && (
        <div style={{ marginTop: 6, paddingTop: 5, borderTop: '1px dashed var(--border)' }}>
          {entry.outputs.map((o) => (
            <div key={o.id} title={`Recorded ${o.output_date}${o.stage_type ? ` · ${o.stage_type}` : ''}${o.lpa != null ? ` · ${fmtNum(o.lpa)} LPA` : ''}`}
              style={{ fontSize: 11, color: o.output_type === 'primary' ? 'var(--text)' : 'var(--text-muted)', lineHeight: 1.4 }}>
              <span style={{ color: '#15803d', fontWeight: 700 }}>✓</span>{' '}
              <b>{fmtNum(o.quantity)}{o.unit ? ` ${shortUnit(o.unit)}` : ''}</b>{' '}
              {o.item_name || 'output'}
              {o.abv != null ? ` @ ${fmtNum(o.abv)}%` : ''}
              {o.output_type === 'byproduct' ? ' (by-product)' : ''}
            </div>
          ))}
        </div>
      )}
    </button>
  );
}

// ── Board ──────────────────────────────────────────────────────────────────
// readOnly: the dashboard's view of this week — no add/edit controls, and
// today's column is highlighted.
export function Board({ board, onAddCard, onEditCard, onAddLane, onEditLane, readOnly = false }) {
  const todayStr = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })();
  const entriesByLaneDay = {};
  for (const e of board.entries) {
    const key = `${e.lane_id}__${e.entry_date.slice(0, 10)}`;
    (entriesByLaneDay[key] ??= []).push(e);
  }

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
        <thead>
          <tr style={{ background: 'var(--surface-2)', borderBottom: '1px solid var(--border)' }}>
            <th style={{ padding: '9px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)', width: 150 }}>Work Area</th>
            {WEEKDAYS.map((d, i) => (
              <th key={d} style={{ padding: '9px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: dateForDay(board.weekStart, i) === todayStr ? 'var(--pld-green)' : 'var(--text-muted)' }}>
                {d} <span style={{ fontWeight: 400, textTransform: 'none' }}>{new Date(dateForDay(board.weekStart, i)).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {board.lanes.map((lane, li) => (
            <tr key={lane.id} style={{ borderBottom: li < board.lanes.length - 1 ? '1px solid var(--border)' : 'none', verticalAlign: 'top' }}>
              <td style={{ padding: '12px 14px', fontWeight: 700, fontSize: 13, color: 'var(--text)' }}>
                {lane.name}
                {!readOnly && <button onClick={() => onEditLane(lane)} title="Rename / remove work area" style={{ display: 'block', marginTop: 4, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--pld-green)', fontSize: 11, padding: 0, textDecoration: 'underline' }}>
                  edit
                </button>}
              </td>
              {WEEKDAYS.map((d, i) => {
                const date = dateForDay(board.weekStart, i);
                const cards = entriesByLaneDay[`${lane.id}__${date}`] || [];
                return (
                  <td key={d} style={{ padding: '10px 10px', borderLeft: '1px solid var(--border)', background: date === todayStr ? 'rgba(34, 57, 31, 0.06)' : undefined }}>
                    {cards.map(c => <Card key={c.id} entry={c} onClick={readOnly ? undefined : () => onEditCard(c)} />)}
                    {readOnly && !cards.length && <div style={{ color: 'var(--text-muted)', fontSize: 12, opacity: 0.5 }}>—</div>}
                    {!readOnly && <button onClick={() => onAddCard(lane.id, date)} style={{ width: '100%', padding: '6px 0', background: 'none', border: '1px dashed var(--border)', borderRadius: 8, cursor: 'pointer', color: 'var(--text-muted)', fontSize: 12 }}>
                      + Add
                    </button>}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {!readOnly && (
        <div style={{ padding: '10px 14px', borderTop: '1px solid var(--border)' }}>
          <Btn size="sm" variant="ghost" onClick={onAddLane}>+ Add Work Area</Btn>
        </div>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function ProductionSchedule() {
  const [weekOf, setWeekOf] = useState(null); // null = current week
  const [addingCard, setAddingCard] = useState(null); // { laneId, date } | null
  const [editingCard, setEditingCard] = useState(null);
  const [addingLane, setAddingLane] = useState(false);
  const [editingLane, setEditingLane] = useState(null);

  const boardUrl = weekOf ? `/production-schedule/board?week=${weekOf}` : '/production-schedule/board';
  const { data: board, loading, refetch } = useApi(boardUrl, [boardUrl]);

  function closeAndRefetch() {
    setAddingCard(null);
    setEditingCard(null);
    setAddingLane(false);
    setEditingLane(null);
    refetch();
  }

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 8px' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>Production Schedule</h1>
        <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: 14 }}>The weekly production planning board — what's happening where, each day</p>
      </div>

      {board && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <Btn size="sm" variant="ghost" onClick={() => setWeekOf(shiftWeek(board.weekStart, -7))}>← Previous week</Btn>
            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{fmtWeekRange(board.weekStart, board.weekEnd)}</span>
            <Btn size="sm" variant="ghost" onClick={() => setWeekOf(shiftWeek(board.weekStart, 7))}>Next week →</Btn>
            {weekOf && <Btn size="sm" variant="ghost" onClick={() => setWeekOf(null)}>Today</Btn>}
          </div>

          {loading ? (
            <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading…</p>
          ) : !board.lanes.length ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
              <p>No work areas on the board yet.</p>
              <Btn onClick={() => setAddingLane(true)}>+ Add Work Area</Btn>
            </div>
          ) : (
            <Board
              board={board}
              onAddCard={(laneId, date) => setAddingCard({ laneId, date })}
              onEditCard={(entry) => setEditingCard(entry)}
              onAddLane={() => setAddingLane(true)}
              onEditLane={(lane) => setEditingLane(lane)}
            />
          )}
        </>
      )}

      {(addingCard || editingCard) && (
        <EntryModal
          entry={editingCard}
          lanes={board.lanes}
          defaultLaneId={addingCard?.laneId}
          defaultDate={addingCard?.date}
          onClose={closeAndRefetch}
          onSaved={closeAndRefetch}
        />
      )}

      {(addingLane || editingLane) && (
        <LaneModal lane={editingLane} onClose={closeAndRefetch} onSaved={closeAndRefetch} />
      )}
    </div>
  );
}
